/**
 * ScrollFadeTabs
 * Wraps a tab bar in a scrollable container that shows edge-fade indicators
 * when content overflows. The active tab is scrolled into view on mount and
 * whenever the active tab changes (via the activeValue prop).
 */

import { useRef, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';

interface ScrollFadeTabsProps {
  children: ReactNode;
  /** Pass the currently active tab value so the component can scroll it into view. */
  activeValue?: string;
  className?: string;
}

export function ScrollFadeTabs({ children, activeValue, className }: ScrollFadeTabsProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);

  const updateFades = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setShowLeftFade(el.scrollLeft > 1);
    setShowRightFade(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateFades();
    el.addEventListener('scroll', updateFades, { passive: true });
    const ro = new ResizeObserver(updateFades);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', updateFades);
      ro.disconnect();
    };
  }, [updateFades]);

  // Scroll the active tab into view whenever activeValue changes
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const activeTab = el.querySelector('[data-state="active"]') as HTMLElement | null;
    activeTab?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  }, [activeValue]);

  return (
    <div className={`relative${className ? ` ${className}` : ''}`}>
      {showLeftFade && (
        <div
          className="pointer-events-none absolute left-0 top-0 bottom-0 w-10 z-10 bg-gradient-to-r from-background to-transparent"
          aria-hidden="true"
        />
      )}
      {showRightFade && (
        <div
          className="pointer-events-none absolute right-0 top-0 bottom-0 w-10 z-10 bg-gradient-to-l from-background to-transparent"
          aria-hidden="true"
        />
      )}
      <div
        ref={scrollRef}
        className="overflow-x-auto"
        style={{ scrollbarWidth: 'none' }}
      >
        {children}
      </div>
    </div>
  );
}
